import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { countries, getCitiesByCountry, City } from '../data/locations';
import AdCard from '../components/AdCard';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { C } from '../theme';

interface Ad {
  id: number;
  title: string;
  description: string;
  price: number;
  country_id: string;
  city_id: string;
  thumbnail: string | null;
  land_type: string;
  area: number;
  author_name: string;
  created_at: string;
}

const glassCardSx = {
  p: { xs: 4, md: 5 },
  backgroundColor: 'rgba(18,18,20,0.92)',
  backdropFilter: 'blur(20px)',
  borderRadius: 4,
  mx: 'auto',
  border: `1px solid ${C.line}`,
  boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
};

const Nekretnine: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [filteredAds, setFilteredAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      fetchAds();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (selectedCountry) {
      setAvailableCities(getCitiesByCountry(Number(selectedCountry)));
      setSelectedCity('');
    } else {
      setAvailableCities([]);
    }
  }, [selectedCountry]);

  const applyFilters = useCallback(() => {
    const filtered = ads.filter(ad => {
      const matchesSearch = ad.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ad.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPrice = (!minPrice || ad.price >= Number(minPrice)) &&
                          (!maxPrice || ad.price <= Number(maxPrice));
      const matchesCountry = !selectedCountry || ad.country_id === selectedCountry;
      const matchesCity = !selectedCity || ad.city_id === selectedCity;

      return matchesSearch && matchesPrice && matchesCountry && matchesCity;
    });

    setFilteredAds(filtered);
  }, [ads, searchTerm, minPrice, maxPrice, selectedCountry, selectedCity]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/ads');
      setAds(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching ads:', err);
      setError('Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchTerm('');
    setMinPrice('');
    setMaxPrice('');
    setSelectedCountry('');
    setSelectedCity('');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" sx={{ backgroundColor: C.bg }}>
        <CircularProgress sx={{ color: C.gold }} />
      </Box>
    );
  }

  return (
    <>
      <SEO
        title="Nekretnine"
        description="Pretražite oglase za zemljište i nekretnine. Pronađite savršeno zemljište za vašu investiciju ili se povežite sa investitorima."
        keywords="nekretnine, zemljiste, investicija, oglasi, pretraga, gradnja, poslovni prostor, platforma za investicije"
        url="/nekretnine"
      />
      <Box sx={{ pt: '70px', backgroundColor: C.bg, minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, rgba(10,10,11,0.86) 0%, rgba(10,10,11,0.72) 100%), url('/images/home.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: C.white,
            py: { xs: 10, md: 16 },
            textAlign: 'center',
            position: 'relative',
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '4rem' },
                lineHeight: 1.1,
                letterSpacing: '-1px',
              }}
            >
              Nekretnine
            </Typography>
            <Typography
              variant="h5"
              sx={{ mb: 6, color: 'rgba(255,255,255,0.85)', maxWidth: 700, mx: 'auto', lineHeight: 1.6, fontWeight: 400, fontSize: { xs: '1.1rem', md: '1.3rem' } }}
            >
              Pronađite savršeno zemljište za vašu investiciju ili povežite se sa investitorima
            </Typography>

            {/* Search Section - Only show if authenticated */}
            {isAuthenticated && (
              <Paper elevation={0} sx={{ ...glassCardSx, maxWidth: 900 }}>
                <Typography variant="h4" sx={{ mb: 3, color: C.white, fontWeight: 700, textAlign: 'center' }}>
                  Pretraži oglase
                </Typography>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      placeholder="Pretraži oglase..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.muted }} /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Država</InputLabel>
                      <Select value={selectedCountry} label="Država" onChange={(e) => setSelectedCountry(e.target.value)}>
                        <MenuItem value="">Sve države</MenuItem>
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button variant="contained" fullWidth size="large" onClick={() => setShowFilters(!showFilters)} startIcon={<FilterIcon />} sx={{ py: 1.5 }}>
                      Filteri
                    </Button>
                  </Grid>
                </Grid>

                {/* Advanced Filters */}
                {showFilters && (
                  <Box sx={{ mt: 4, pt: 4, borderTop: `1px solid ${C.line}` }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Minimalna cena"
                          type="number"
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Maksimalna cena"
                          type="number"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Grad</InputLabel>
                          <Select value={selectedCity} label="Grad" onChange={(e) => setSelectedCity(e.target.value)} disabled={!selectedCountry}>
                            <MenuItem value="">Svi gradovi</MenuItem>
                            {availableCities.map((city) => (
                              <MenuItem key={city.id} value={city.id}>
                                {city.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button variant="outlined" fullWidth onClick={handleReset} sx={{ py: 1.5 }}>
                          Resetuj
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            )}

            {/* Login Prompt for non-authenticated users */}
            {!isAuthenticated && (
              <Paper elevation={0} sx={{ ...glassCardSx, maxWidth: 600, textAlign: 'center' }}>
                <LockIcon sx={{ fontSize: 60, color: C.gold, mb: 3 }} />
                <Typography variant="h4" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
                  Prijavite se za pristup oglasima
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: C.muted, lineHeight: 1.6 }}>
                  Da biste videli dostupne oglase i povezali se sa investitorima ili vlasnicima zemljišta,
                  potrebno je da se prijavite na svoj nalog.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button variant="contained" size="large" onClick={() => navigate('/login')} sx={{ px: 4, py: 1.5 }}>
                    Prijavi se
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => navigate('/register')} sx={{ px: 4, py: 1.5 }}>
                    Registruj se
                  </Button>
                </Box>
              </Paper>
            )}
          </Container>
        </Box>

        {/* Results Section - Only show if authenticated */}
        {isAuthenticated && (
          <Container maxWidth="lg" sx={{ py: 8 }}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, color: C.white }}>
                Dostupni oglasi
              </Typography>
              <Typography variant="h6" sx={{ color: C.muted, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                Pronađite savršeno zemljište za vašu investiciju ili povežite se sa investitorima
              </Typography>
            </Box>

            {error && (
              <Typography color="error" textAlign="center" sx={{ mb: 4 }}>
                {error}
              </Typography>
            )}

            {filteredAds.length === 0 && !loading ? (
              <Box textAlign="center" sx={{ py: 8 }}>
                <Typography variant="h6" sx={{ mb: 2, color: C.white }}>
                  Nema pronađenih oglasa
                </Typography>
                <Typography sx={{ color: C.muted }}>
                  Pokušajte da promenite kriterijume pretrage
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={4}>
                {filteredAds.map((ad) => (
                  <Grid item xs={12} sm={6} md={4} key={ad.id}>
                    <AdCard
                      id={ad.id}
                      title={ad.title}
                      price={ad.price}
                      country_id={ad.country_id}
                      city_id={ad.city_id}
                      thumbnail={ad.thumbnail}
                      land_type={ad.land_type}
                      area={ad.area}
                      author_name={ad.author_name}
                      created_at={ad.created_at}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        )}
      </Box>
    </>
  );
};

export default Nekretnine;
